import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAuthLoading: boolean;   // Neu: warten bis Auth-Status bekannt ist
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession]       = useState<Session | null>(null);
  const [user, setUser]             = useState<User | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true); // Startet als true

  useEffect(() => {
    let isMounted = true;
    const authTimeout = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Auth-Check Timeout')), 2500);
    });

    // 1) Aktuelle Session sofort holen
    Promise.race([supabase.auth.getSession(), authTimeout])
      .then(({ data }) => {
        if (!isMounted) return;
        setSession(data.session ?? null);
        setUser(data.session?.user ?? null);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.warn('Auth-Status konnte nicht geladen werden:', err);
        setSession(null);
        setUser(null);
      })
      .finally(() => {
        if (!isMounted) return;
        setIsAuthLoading(false); // Auth-Check abgeschlossen
      });

    // 2) Auf spätere Auth-Änderungen reagieren (Login, Logout, Token-Refresh)
    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      setIsAuthLoading(false);
    });

    return () => {
      isMounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const logout = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, isAuthLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
